import { Axios } from "api/axios";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { exportGstCSV, fetchGsts as OnfetchGst } from "slices/gst/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Gst = () => {
  document.title = "GST List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "gst",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectGst = createSelector(
    (state: any) => state.GST,
    (gst) => ({
      Gsts: gst.gstList,
      rowCount: gst.rowCount,
      loading: gst.loading,
      error: gst.error,
    })
  );

  const { Gsts, rowCount, loading, error } = useSelector(selectGst);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.gst
  );

  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchGst({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedUnit = {
        id: pkg.gst_id,
        is_active: updatedStatus,
      };
      const payload = { is_active: updatedUnit.is_active };
      setIsStatusUpdateLoading(true);
      const response: any = await Axios.patch(
        `/master/gst/${updatedUnit.id}/update/`,
        payload
      );
      // dispatch(onUpdateStatusInGst(updatedUnit))
      //   .unwrap()
      //   .then(() => {
      //     console.log({
      //       page: pageIndex + 1,
      //       pageSize,
      //       sort: sortField,
      //       order: sortField ? sortDirection : "",
      //       search,
      //       filters: filterValue,
      //     });
      toast.success(
        `Gst ${updatedStatus ? "activated" : "deactivated"} successfully`
      );
      dispatch(
        OnfetchGst({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: colValue,
        })
      );
      // })
      // .catch((e: any) => {
      //   throw e;
      // });
    } catch (err: any) {
      toast.error("Failed to Update status !");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportGstCSV({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <EditButton to={`/edit-gst/${row?.original?.gst_id}`} />
          ),
        },
      ]
    : [];

  const columns = [
    {
      header: "Sr. No.",
      id: "sr_no", // important: since there's no accessorKey
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: any }) => {
        return (
          row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
        );
      },
    },

    {
      header: "GST %",
      accessorKey: "gst_percent",
      enableSorting: true,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl: "/master/gst/dropdown/gst_percent",
      cell: ({ getValue }: any) => `${String(getValue())}  %`,
    },

    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const gst = row.original;
        return (
          <StatusToggle
            isActive={gst.is_active}
            onChange={() => {
              handleStatusToggle(gst);
            }}
            disabled={!canUpdate}
          />
        );
      },
    },

    {
      header: "Remarks",
      // id: "remarks", // used `id` instead of accessorKey since data isn't ready yet
      accessorKey: "remark", //*// use Once API starts returning a remark field
      enableSorting: false,
      enableColumnFilter: false,
      maxSize: 250,
      cell: ({ row }: { row: any }) => (
        <TruncatedTextCell
          text={row?.original?.remark}
          maxWidth="300px"
          maxLines={4}
          rowIndex={row.index}
          tooltipId={`remarks-tooltip-${row.index}`}
        />
      ),
    },

    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by_user.username ?? "N/A";
        const date = row.original.created_at
          ? new Date(row.original.created_at).toISOString().split("T")[0]
          : "N/A";
        return `${name}-${date}`;
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by_user.username ?? "N/A";
        const date = row.original.updated_at
          ? new Date(row.original.updated_at).toISOString().split("T")[0]
          : "N/A";
        return `${name}-${date}`;
      },
    },
    ...DynamicAction,
  ];

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">GST</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add GST"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-gst"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <TableContainer
                  columns={columns}
                  // data={gstTableData || []}
                  data={Gsts || []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search Gst..."
                  manualSorting={true}
                  colValue={setColValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Gst;
